/**
 * Version History Drawer
 *
 * Shows version history for a text field with view and revert actions.
 */

import { useState } from 'react';
import { Drawer, List, Tag, Button, Space, Typography, Modal, message, Spin } from 'antd';
import { HistoryOutlined, EyeOutlined, RollbackOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { contentVersionsApi, type ContentVersion } from '@api/content-versions';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

interface VersionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  entityType: 'template' | 'instance';
  entityId: string;
  fieldName?: string;
  onReverted?: (content: string) => void;
}

const SOURCE_COLORS: Record<string, string> = {
  manual: 'default',
  ai: 'purple',
  autosave: 'blue',
};

export default function VersionHistoryDrawer({
  open,
  onClose,
  entityType,
  entityId,
  fieldName = 'description',
  onReverted,
}: VersionHistoryDrawerProps) {
  const [previewVersion, setPreviewVersion] = useState<ContentVersion | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['content-versions', entityType, entityId, fieldName],
    queryFn: () => contentVersionsApi.listVersions(entityType, entityId, fieldName),
    enabled: open,
  });

  const revertMutation = useMutation({
    mutationFn: (version: number) =>
      contentVersionsApi.revertToVersion(entityType, entityId, version, fieldName),
    onSuccess: (result) => {
      message.success('Reverted successfully');
      queryClient.invalidateQueries({ queryKey: ['content-versions', entityType, entityId, fieldName] });
      onReverted?.(result.content);
      onClose();
    },
    onError: () => {
      message.error('Failed to revert');
    },
  });

  const handleRevert = (version: number) => {
    Modal.confirm({
      title: 'Revert to this version?',
      content: 'The current content will be saved as a new version before reverting.',
      okText: 'Revert',
      onOk: () => revertMutation.mutate(version),
    });
  };

  return (
    <>
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            Version History
          </Space>
        }
        open={open}
        onClose={onClose}
        width={380}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <List
            dataSource={data?.items || []}
            locale={{ emptyText: 'No version history yet' }}
            renderItem={(item: ContentVersion) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => setPreviewVersion(item)}
                  />,
                  <Button
                    key="revert"
                    type="text"
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() => handleRevert(item.version)}
                  />,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text>v{item.version}</Text>
                      <Tag color={SOURCE_COLORS[item.change_source] || 'default'} style={{ fontSize: 11 }}>
                        {item.change_source}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(item.created_at).fromNow()}
                      </Text>
                      {item.created_by_name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          by {item.created_by_name}
                        </Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                        {(item.content || '').slice(0, 80)}...
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      {/* Preview Modal */}
      <Modal
        title={`Version ${previewVersion?.version}`}
        open={!!previewVersion}
        onCancel={() => setPreviewVersion(null)}
        footer={[
          <Button key="close" onClick={() => setPreviewVersion(null)}>Close</Button>,
          <Button
            key="revert"
            type="primary"
            icon={<RollbackOutlined />}
            onClick={() => {
              if (previewVersion) {
                handleRevert(previewVersion.version);
                setPreviewVersion(null);
              }
            }}
          >
            Revert to This
          </Button>,
        ]}
        width={600}
      >
        <Paragraph
          style={{
            maxHeight: 400,
            overflow: 'auto',
            background: 'var(--ant-color-bg-container-disabled, #f5f5f5)',
            padding: 16,
            borderRadius: 8,
            whiteSpace: 'pre-wrap',
          }}
        >
          {previewVersion?.content}
        </Paragraph>
      </Modal>
    </>
  );
}
